// Modal for the Reactivate flow (PR #11). All three options default
// OFF -- pure unpause is the safest baseline. Operator opts into
// re-enroll / resume-replied-elsewhere / resume-paused-manual
// explicitly via the "Advanced options" expander.
//
//   1. Re-enroll new list members (default off): runs enrollment
//      again and pulls newly-matching contacts.
//   2. Resume recipients paused-by-replied-elsewhere (default off):
//      flips paused_replied_elsewhere -> pending. A fresh cycle is
//      a new conversation, but flipping these silently was leading
//      to operator confusion; explicit opt-in now.
//   3. Resume recipients paused-by-admin (default off): admin paused
//      them deliberately; don't override without intent.
//
// Surfaces the DQ-8 warning "campaign has no step beyond {max_step};
// sending won't fire until you add one" only when the source campaign
// has zero recipients in pending/throttled/in_progress AND no NEW
// step has been added. The frontend can't easily detect "no new step"
// pre-mutation, so we rely on the post-mutation result's
// `no_active_recipients_after` flag instead — caller surfaces it via
// alert text.

import { useState } from 'react';

import type { Campaign, ReactivateCampaignRequest } from '../../types/outreach';
import styles from '../pages/OutreachPage.module.css';


export function ReactivateConfirm({
  campaign,
  onCancel,
  onConfirm,
  isPending,
}: {
  campaign: Campaign;
  onCancel: () => void;
  onConfirm: (body: ReactivateCampaignRequest) => Promise<void>;
  isPending: boolean;
}) {
  // All defaults OFF -- pure unpause is the safe baseline; advanced
  // options are explicit opt-in via the expander below.
  const [reEnroll, setReEnroll] = useState(false);
  const [resumeRepliedElsewhere, setResumeRepliedElsewhere] = useState(false);
  const [resumePausedManual, setResumePausedManual] = useState(false);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          padding: 24,
          borderRadius: 8,
          maxWidth: 520,
          fontFamily: "'DM Sans', sans-serif",
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: 18 }}>
          Reactivate {campaign.name}?
        </h3>

        <div className={styles.helpText} style={{ marginBottom: 16 }}>
          The campaign is currently <strong>{campaign.status}</strong>.
          Reactivating moves it back to <strong>active</strong>; the
          sequencer will dispatch eligible recipients on its next tick.
          Recipients in <code>finished_no_more_steps</code> are left
          alone — they only re-receive if you add a new step from the
          Steps tab afterward.
        </div>

        {/* Native <details> expander -- no JS, accessible, collapsed
            by default. Operator only opens it when they need to
            override the safe defaults. The summary indicates how
            many options are currently enabled so they don't have to
            open it just to check. */}
        <details
          style={{
            marginBottom: 4,
            border: '1px solid #E5E5E0',
            borderRadius: 6,
            padding: '8px 12px',
          }}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              userSelect: 'none',
            }}
          >
            Advanced options{' '}
            {(() => {
              const n = (reEnroll ? 1 : 0)
                + (resumeRepliedElsewhere ? 1 : 0)
                + (resumePausedManual ? 1 : 0);
              return n > 0 ? (
                <span style={{ fontWeight: 400, color: '#666' }}>
                  ({n} enabled)
                </span>
              ) : (
                <span style={{ fontWeight: 400, color: '#999' }}>
                  (none enabled — pure unpause)
                </span>
              );
            })()}
          </summary>
          <div style={{ marginTop: 12 }}>
            <CheckboxRow
              checked={reEnroll}
              onChange={setReEnroll}
              label="Re-enroll new list members"
              help="Runs the standard enrollment flow. Pulls newly-matching contacts from Affinity and adds them as fresh recipients. Existing recipients are unaffected."
            />
            <CheckboxRow
              checked={resumeRepliedElsewhere}
              onChange={setResumeRepliedElsewhere}
              label="Resume recipients paused by reply-elsewhere"
              help="They previously replied so the engine paused them. A fresh cycle means a new conversation — flip them back to pending."
            />
            <CheckboxRow
              checked={resumePausedManual}
              onChange={setResumePausedManual}
              label="Resume recipients paused manually"
              help="Off by default — admin paused them deliberately, don't override without intent."
            />
          </div>
        </details>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button type="button" className={styles.button} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isPending}
            onClick={() =>
              onConfirm({
                re_enroll: reEnroll,
                resume_replied_elsewhere: resumeRepliedElsewhere,
                resume_paused_manual: resumePausedManual,
              })
            }
          >
            {isPending ? 'Reactivating…' : 'Reactivate'}
          </button>
        </div>
      </div>
    </div>
  );
}


function CheckboxRow({
  checked,
  onChange,
  label,
  help,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  help: string;
}) {
  return (
    <label
      style={{
        display: 'block',
        marginBottom: 12,
        cursor: 'pointer',
        fontSize: 13,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ marginTop: 3 }}
        />
        <div>
          <div style={{ fontWeight: 600 }}>{label}</div>
          <div className={styles.helpText} style={{ marginTop: 2 }}>
            {help}
          </div>
        </div>
      </div>
    </label>
  );
}
